<template>
  <div class="qr-reader-container">
    <div class="logout-wrapper">
      <f-button type="secondary" @click="handleLogout">تسجيل الخروج</f-button>
    </div>
    <div class="background-image">
      <span class="overlay"></span>
      <img src="@/assets/Image/truck.png" alt="Truck Background" />
    </div>

    <div class="scanner-title">
      <h2>مسح الرمز</h2>
      <p>قم بتوجيه الكاميرا نحو الرمز</p>
    </div>

    <div class="scanner-wrapper" :class="{ flip: isFlipping }">
      <div class="scanner">
        <qrcode-stream
          v-if="!certificateReady"
          @decode="onDecode"
          :constraints="{ facingMode }"
        />
        <div v-else class="scan-success">
          <i class="ri-checkbox-circle-fill"></i>
          <p>تم مسح الرمز بنجاح</p>
        </div>
      </div>
    </div>

    <div v-if="loading" class="status-message">جاري جلب بيانات الشهادة...</div>

    <div v-else-if="error" class="status-message error">{{ error }}</div>

    <div v-else-if="certificateReady" class="actions-card">
      <p class="actions-title">الشهادة جاهزة</p>
      <p class="actions-subtitle">يمكنك استعراض الشهادة أو تحميلها</p>
      <div class="actions-buttons">
        <f-button type="primary" @click="handleView">استعراض الشهادة</f-button>
        <f-button type="secondary" @click="handleDownload">تحميل الشهادة</f-button>
        <f-button type="secondary" @click="resetScanner">مسح رمز جديد</f-button>
      </div>
    </div>

    <div class="dark-overlay"></div>
  </div>
</template>

<script>
import { QrcodeStream } from "vue3-qrcode-reader";
import {
  extractCertificateId,
  fetchCertificate,
} from "@/Services/CertificateService";
import {
  downloadStoredFile,
  previewStoredFile,
} from "@/Services/StoredFileService";
import { logout } from "@/Utils/logout";

export default {
  components: {
    QrcodeStream,
  },
  data() {
    return {
      certificateId: "",
      fileUrl: "",
      fileName: "",
      facingMode: "environment",
      isFlipping: false,
      loading: false,
      error: "",
      certificateReady: false,
      isProcessingScan: false,
    };
  },
  methods: {
    handleLogout() {
      logout(this.$router);
    },
    resetScanner() {
      this.certificateId = "";
      this.fileUrl = "";
      this.fileName = "";
      this.error = "";
      this.certificateReady = false;
      this.isProcessingScan = false;
      this.loading = false;
    },
    onDecode(decoded) {
      if (decoded && !this.isProcessingScan && !this.certificateReady) {
        this.loadCertificate(decoded);
      }
    },
    async loadCertificate(rawId) {
      const extractedId = extractCertificateId(rawId);
      this.isProcessingScan = true;
      this.loading = true;
      this.error = "";
      this.certificateReady = false;

      try {
        const data = await fetchCertificate(extractedId);

        if (!data.fileUrl) {
          this.error = "الرابط غير متاح أو الشهادة غير موجودة.";
          return;
        }

        this.certificateId = extractedId;
        this.fileUrl = data.fileUrl;
        this.fileName = data.fileName;
        this.certificateReady = true;
      } catch (err) {
        console.error("فشل في جلب مسار الشهادة:", err);
        this.error = "حدث خطأ أثناء محاولة جلب الشهادة.";
      } finally {
        this.loading = false;
      }
    },
    handleView() {
      previewStoredFile(this.fileUrl, this.fileName);
    },
    handleDownload() {
      downloadStoredFile(this.fileUrl, this.fileName);
    },
  },
};
</script>

<style scoped>
.logout-wrapper {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 10;
}

.logout-wrapper :deep(.btn) {
  width: auto;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary-color);
}

.qr-reader-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: var(--white);
  gap: 20px;
  text-align: center;
  overflow: hidden;
  padding: 24px 16px 32px;
}

.background-image {
  position: absolute;
  inset: 0;
  z-index: -2;
}

.background-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: calc(100% + 180px) 100%;
  filter: blur(5px);
}

.overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    179.6deg,
    rgba(255, 255, 255, 0) 8.08%,
    rgba(219, 222, 243, 0.06) 32.1%,
    rgba(210, 217, 236, 0.09) 38.43%,
    rgba(76, 127, 114, 0.52) 58.77%,
    rgba(0, 76, 44, 0.76) 103.51%
  );
}

.scanner-title h2 {
  font-size: 1.75rem;
  font-weight: bold;
}

.scanner-title p {
  font-size: 1rem;
  margin-top: 0.5rem;
}

.scanner-wrapper {
  perspective: 1000px;
  width: 295px;
  height: 320px;
}

.scanner {
  width: 100%;
  height: 100%;
  padding: 10px;
  background: url("@/assets/Image/Scanner Container.png") center/contain
    no-repeat;
  transition: transform 0.6s ease;
  transform-style: preserve-3d;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scanner-wrapper.flip .scanner {
  transform: rotateY(180deg);
}

.scan-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: var(--white);
}

.scan-success i {
  font-size: 56px;
  color: #4ade80;
}

.status-message {
  font-size: 0.95rem;
  color: var(--white);
  padding: 0 16px;
}

.status-message.error {
  color: #fecaca;
}

.actions-card {
  width: min(100%, 420px);
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 20px;
  color: #111827;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  z-index: 2;
}

.actions-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.actions-subtitle {
  font-size: 0.9rem;
  color: var(--dark-gray2);
  margin-bottom: 16px;
}

.actions-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.actions-buttons :deep(.btn) {
  width: 100%;
}

.dark-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1;
}

@media (max-width: 768px) {
  .scanner-wrapper {
    width: 260px;
    height: 290px;
  }

  .actions-card {
    margin: 0 8px;
  }
}
</style>
